import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";

import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EnergySummaryCard } from "@/components/consultation/tabs/nutrition/components/energy-summary-card";
import { FeaturedNutrientsCard } from "@/components/consultation/tabs/nutrition/components/featured-nutrients-card";
import { DetailedNutrientsPanel } from "@/components/consultation/tabs/nutrition/components/detailed-nutrients-panel";
import { AddFoodDialog } from "@/components/consultation/tabs/nutrition/components/add-food-dialog";
import { AddFoodDialogWithSelector } from "@/components/consultation/tabs/nutrition/components/add-food-dialog-with-selector";
import { AddNoteDialog } from "@/components/consultation/tabs/nutrition/components/add-note-dialog";
import { AddNoteDialogWithSelector } from "@/components/consultation/tabs/nutrition/components/add-note-dialog-with-selector";
import { NoteRow } from "@/components/consultation/tabs/nutrition/components/note-row";
import { NutrientReadings } from "@/components/consultation/tabs/nutrition/components/nutrient-readings";
import {
  AddFeedDialog,
  TubeFeedingSection,
} from "@/components/consultation/tabs/nutrition/components/tube-feeding-section";
import {
  MEAL_TIME_DISPLAY,
  quantityPatternFor,
  computeEntryKcal,
} from "@/components/consultation/tabs/nutrition/components/meal-constants";
import {
  Loader2,
  Plus,
  Trash2,
  Apple,
  StickyNote,
  Copy,
  MoveVertical,
  AlertCircle,
  AlertTriangle,
  MoreVertical,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useNutritionEvaluationContext } from "../NutritionEvaluationContext";
import {
  useDietRecall,
  useAddMealEntry,
  useUpdateMealEntry,
  useDeleteMealEntry,
  useMoveNutritionEntry,
  useFoodServings,
  useHealthProfile,
} from "@/hooks/useNutritionEvaluation";
import type {
  MealData,
  MealTime,
  MealEntry,
  FeedData,
  FeedTime,
  FeedEntry,
} from "@/types/nutrition";
import type {
  MoveTarget,
  MoveTargetContext,
} from "@/lib/nutrition-evaluation-api";
import { toast } from "sonner";

// =============================================================================
// Meal Entry Row (matching Flutter Entry display)
// =============================================================================

interface MealEntryRowProps {
  entry: MealEntry | FeedEntry;
  appointmentId: string;
  isTubeFeeding: boolean;
  isCompleted: boolean;
  isMealPlan?: boolean;
  mealTimes?: MealTime[];
  feedGroups?: FeedTime[];
  mealGroupLabel?: string;
  externalMoveTargets?: MoveTarget[];
}

const MealEntryRow: React.FC<MealEntryRowProps> = ({
  entry,
  appointmentId,
  isTubeFeeding,
  isCompleted,
  isMealPlan = false,
  mealTimes = [],
  feedGroups = [],
  mealGroupLabel,
  externalMoveTargets = [],
}) => {
  // Tube feed entries can carry `feed_item` (volume_ml) or `food_item`
  // (qty + serving). Detect the formula-feed shape early — it changes which
  // field we edit and which control we render.
  const isFeedItem = isTubeFeeding && !!(entry as FeedEntry).feed_item;
  const parentGroupId =
    (entry as MealEntry).meal_group ?? (entry as FeedEntry).feed_group;

  const serverEditValue = isFeedItem
    ? (entry as FeedEntry).volume_ml?.toString() || ""
    : entry.quantity?.toString() || "";
  const [localEditValue, setLocalEditValue] = useState(serverEditValue);
  const [isDirty, setIsDirty] = useState(false);
  const [servingOpen, setServingOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Pass isMealPlan to use correct API endpoint
  const deleteEntry = useDeleteMealEntry(
    appointmentId,
    isTubeFeeding,
    isMealPlan
  );
  const updateEntry = useUpdateMealEntry(
    appointmentId,
    isTubeFeeding,
    isMealPlan
  );

  // Re-sync local value to server value when not dirty (e.g. another field
  // updated and the entry rehydrated, or save just succeeded).
  useEffect(() => {
    if (!isDirty) setLocalEditValue(serverEditValue);
  }, [serverEditValue, isDirty]);
  const addEntry = useAddMealEntry(appointmentId, isTubeFeeding, isMealPlan);
  const { mutate: moveEntryMutate, isPending: isMovePending } =
    useMoveNutritionEntry(appointmentId);
  const { data: servings = [] } = useFoodServings(
    entry.food_item || 0,
    !!entry.food_item && servingOpen && !isFeedItem
  );

  // Tube feeds are limited to one food entry per feed, so skip any target feed
  // group that already has one — Copy/Move would otherwise bypass the
  // "Add food" disabled state enforced in tube-feeding-section.
  const copyToTargets = isTubeFeeding
    ? feedGroups.filter(
        (fg) =>
          fg.id !== parentGroupId &&
          !fg.entries.some(
            (e) =>
              !e.is_comment &&
              (e.food_item || e.food_item_details || e.feed_item_details)
          )
      )
    : mealTimes.filter((mt) => mt.id !== parentGroupId);

  const moveTargets = useMemo<MoveTarget[]>(
    () => [
      ...copyToTargets.map((target) => ({
        id: target.id,
        context: (isMealPlan
          ? "meal_plan"
          : "diet_recall") as MoveTargetContext,
        label: isTubeFeeding
          ? (target as FeedTime).label || (target as FeedTime).time
          : MEAL_TIME_DISPLAY[(target as MealTime).meal_time] ||
            (target as MealTime).meal_time,
      })),
      ...externalMoveTargets,
    ],
    [copyToTargets, externalMoveTargets, isMealPlan, isTubeFeeding]
  );

  const buildCopyPayload = useCallback(
    (targetGroupId: string): Parameters<typeof addEntry.mutate>[0] | null => {
      if (isTubeFeeding) {
        if (isFeedItem) {
          const feedEntry = entry as FeedEntry;
          if (!feedEntry.feed_item || !feedEntry.volume_ml) return null;
          return {
            feed_group: targetGroupId,
            feed_item: feedEntry.feed_item,
            volume_ml: feedEntry.volume_ml,
          } as Parameters<typeof addEntry.mutate>[0];
        }
        if (!entry.food_item || !entry.serving_size) return null;
        return {
          feed_group: targetGroupId,
          food_item: entry.food_item,
          serving_size: entry.serving_size,
          quantity: entry.quantity,
        } as Parameters<typeof addEntry.mutate>[0];
      }
      if (!entry.food_item || !entry.serving_size) return null;
      return {
        meal_group: targetGroupId,
        food_item: entry.food_item,
        serving_size: entry.serving_size,
        quantity: entry.quantity,
      } as Parameters<typeof addEntry.mutate>[0];
    },
    [addEntry, entry, isFeedItem, isTubeFeeding]
  );

  const handleCopyTo = useCallback(
    (targetGroupId: string) => {
      const payload = buildCopyPayload(targetGroupId);
      if (!payload) return;
      addEntry.mutate(payload);
    },
    [addEntry, buildCopyPayload]
  );

  const handleMoveTo = useCallback(
    (target: MoveTarget) => {
      moveEntryMutate(
        {
          entry_type: isTubeFeeding ? "feed" : "meal",
          source_context: isMealPlan ? "meal_plan" : "diet_recall",
          target_context: target.context,
          source_entry_id: entry.id,
          target_group_id: target.id,
        },
        {
          onSuccess: () => toast.success("Moved successfully"),
          onError: (error) =>
            toast.error(
              (error as { message?: string })?.message ||
                "Move failed. The item was not changed."
            ),
        }
      );
    },
    [entry.id, isMealPlan, isTubeFeeding, moveEntryMutate]
  );

  const handleServingChange = useCallback(
    (value: string) => {
      const servingId = parseInt(value);
      if (!Number.isNaN(servingId) && servingId !== entry.serving_size) {
        updateEntry.mutate(
          {
            entryId: entry.id,
            data: { serving_size: servingId },
          },
          {
            onSuccess: () => toast.success("Serving size updated"),
            onError: () => toast.error("Failed to update serving size"),
          }
        );
      }
    },
    [entry.id, entry.serving_size, updateEntry]
  );

  const servingLabel =
    entry.serving_details?.description ||
    entry.serving_details?.serving_unit?.name ||
    "Serving";

  // Local edit only — the API call fires when the user commits via the
  // ✅ button (or Enter). Avoids per-keystroke recalculation on the backend.
  // Whole servings only when the entry's food contains something indivisible.
  // Stated by the API on food_item_details — never inferred here from the food
  // being a supplement, since a weighed whey scoop halves perfectly well.
  const wholeServingsOnly = Boolean(
    (
      entry.food_item_details as
        | { requires_whole_servings?: boolean }
        | undefined
    )?.requires_whole_servings
  );

  const handleEditValueChange = useCallback(
    (value: string) => {
      if (!quantityPatternFor(wholeServingsOnly).test(value)) return;
      setLocalEditValue(value);
      setIsDirty(value !== serverEditValue);
    },
    [serverEditValue, wholeServingsOnly]
  );

  const handleEditValueCommit = useCallback(() => {
    if (!isDirty) return;
    if (localEditValue === "" || localEditValue.endsWith(".")) {
      setLocalEditValue(serverEditValue);
      setIsDirty(false);
      return;
    }
    const data = isFeedItem
      ? { volume_ml: localEditValue }
      : { quantity: localEditValue };
    updateEntry.mutate(
      { entryId: entry.id, data },
      { onSuccess: () => setIsDirty(false) }
    );
  }, [
    isDirty,
    isFeedItem,
    localEditValue,
    serverEditValue,
    entry.id,
    updateEntry,
  ]);

  const handleEditValueCancel = useCallback(() => {
    setLocalEditValue(serverEditValue);
    setIsDirty(false);
  }, [serverEditValue]);

  const handleConfirmDelete = useCallback(() => {
    deleteEntry.mutate(entry.id, {
      onSuccess: () => setShowDeleteConfirm(false),
    });
  }, [deleteEntry, entry.id]);

  if (entry.is_comment) {
    return (
      <NoteRow
        entryId={entry.id}
        comment={entry.comment ?? ""}
        isAttached={false}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={isMealPlan}
        isCompleted={isCompleted}
        mealGroupLabel={mealGroupLabel}
      />
    );
  }

  const itemLabel = isFeedItem
    ? (entry as FeedEntry).feed_item_details?.name ||
      `Feed #${(entry as FeedEntry).feed_item}`
    : entry.food_item_details?.name || `Food #${entry.food_item}`;
  const attachedComment =
    isTubeFeeding && entry.comment?.trim() ? entry.comment.trim() : "";

  return (
    <>
      <div className="grid grid-cols-[16px_1fr_104px_220px_80px_32px] items-center gap-3 px-3 py-2 hover:bg-muted/30 rounded-lg transition-colors group">
        <Apple className="size-4 fill-primary text-primary" />
        <div className="min-w-0">
          <div className="z-body break-words leading-snug">{itemLabel}</div>
        </div>
        {!isCompleted ? (
          <div className="flex items-center gap-1">
            <Input
              type="text"
              value={localEditValue}
              onChange={(e) => handleEditValueChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleEditValueCommit();
                if (e.key === "Escape") handleEditValueCancel();
              }}
              className={cn(
                "h-9 px-2 text-center flex-1 min-w-0",
                isDirty && "border-primary ring-2 ring-primary/20 bg-primary/5"
              )}
              disabled={updateEntry.isPending}
            />
            {isDirty && (
              <Button
                type="button"
                size="icon-sm"
                onClick={handleEditValueCommit}
                disabled={updateEntry.isPending}
                aria-label={isFeedItem ? "Save volume" : "Save quantity"}
              >
                {updateEntry.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </Button>
            )}
          </div>
        ) : (
          <span className="z-body-md text-center">
            {isFeedItem ? (entry as FeedEntry).volume_ml : entry.quantity}
          </span>
        )}
        {isFeedItem ? (
          <span className="z-body text-muted-foreground">ml</span>
        ) : !isCompleted ? (
          <Select
            open={servingOpen}
            onOpenChange={setServingOpen}
            value={entry.serving_size?.toString() ?? ""}
            onValueChange={handleServingChange}
            disabled={updateEntry.isPending}
          >
            <SelectTrigger className="data-[size=default]:h-auto min-h-9 w-full whitespace-normal py-1.5 text-left [&>[data-slot=select-value]]:line-clamp-none [&>[data-slot=select-value]]:text-left">
              <SelectValue placeholder="Serving">{servingLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {servings.map((serving) => (
                <SelectItem key={serving.id} value={serving.id.toString()}>
                  {serving.description ||
                    serving.serving_unit?.name ||
                    "Serving"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="z-body text-muted-foreground break-words">
            {servingLabel}
          </span>
        )}
        <div className="z-body-md text-right z-numeric flex items-center justify-end gap-1.5">
          {updateEntry.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : (
            <span>{Math.round(computeEntryKcal(entry)) || "—"} kcal</span>
          )}
        </div>
        {!isCompleted ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Row actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(copyToTargets.length > 0 || moveTargets.length > 0) &&
              ((isFeedItem &&
                (entry as FeedEntry).feed_item &&
                (entry as FeedEntry).volume_ml) ||
                (!isFeedItem && entry.food_item && entry.serving_size)) ? (
                <>
                  {copyToTargets.length > 0 && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Copy className="h-4 w-4" />
                        Copy to
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {copyToTargets.map((target) => (
                          <DropdownMenuItem
                            key={target.id}
                            onSelect={() => handleCopyTo(target.id)}
                          >
                            {isTubeFeeding
                              ? (target as FeedTime).label ||
                                (target as FeedTime).time
                              : MEAL_TIME_DISPLAY[
                                  (target as MealTime).meal_time
                                ] || (target as MealTime).meal_time}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {moveTargets.length > 0 && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <MoveVertical className="h-4 w-4" />
                        Move to
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {moveTargets.map((target) => (
                          <DropdownMenuItem
                            key={`${target.context}-${target.id}`}
                            onSelect={() => handleMoveTo(target)}
                            disabled={isMovePending}
                          >
                            {target.context === "meal_plan"
                              ? `Meal Plan - ${target.label}`
                              : target.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuItem
                variant="destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setShowDeleteConfirm(true);
                }}
                disabled={deleteEntry.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span />
        )}
      </div>
      {attachedComment && (
        <NoteRow
          entryId={entry.id}
          comment={attachedComment}
          isAttached
          appointmentId={appointmentId}
          isTubeFeeding={isTubeFeeding}
          isMealPlan={isMealPlan}
          isCompleted={isCompleted}
          mealGroupLabel={mealGroupLabel}
        />
      )}
      <DeleteEntryConfirm
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        isLoading={deleteEntry.isPending}
        itemLabel={itemLabel}
        mealGroupLabel={mealGroupLabel}
      />
    </>
  );
};

// =============================================================================
// Delete entry confirmation dialog
// =============================================================================

interface DeleteEntryConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  itemLabel: string;
  mealGroupLabel?: string;
}

const DeleteEntryConfirm: React.FC<DeleteEntryConfirmProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  itemLabel,
  mealGroupLabel,
}) => {
  const fromPart = mealGroupLabel ? `"${mealGroupLabel}"` : "the food group";
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="border-border/60">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Remove the food item from {fromPart}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <ul className="list-disc pl-5">
              <li>{itemLabel}</li>
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onConfirm();
            }}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// =============================================================================
// Meal Time Card (matching Flutter MealTile)
// =============================================================================

interface MealTimeCardProps {
  mealTime: MealTime;
  index: number;
  appointmentId: string;
  isTubeFeeding: boolean;
  isCompleted: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  isMealPlan?: boolean;
  planIndex?: number;
  mealTimes?: MealTime[];
  externalMoveTargets?: MoveTarget[];
}

const MealTimeCard: React.FC<MealTimeCardProps> = ({
  mealTime,
  index: _index,
  appointmentId,
  isTubeFeeding,
  isCompleted,
  isExpanded,
  onToggle,
  isMealPlan = false,
  planIndex,
  mealTimes = [],
  externalMoveTargets = [],
}) => {
  const [showAddFood, setShowAddFood] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);

  const summary = mealTime.meal_nutrients_summary;
  const mealTimeLabel =
    MEAL_TIME_DISPLAY[mealTime.meal_time] || mealTime.meal_time;
  const hasFoodEntries = mealTime.entries.some((e) => !e.is_comment);
  const headerEnergy = useMemo(
    () => mealTime.entries.reduce((sum, e) => sum + computeEntryKcal(e), 0),
    [mealTime.entries]
  );
  const mealGroupOptions = useMemo(
    () =>
      mealTimes.map((mt) => ({
        id: mt.id,
        label: MEAL_TIME_DISPLAY[mt.meal_time] || mt.meal_time,
      })),
    [mealTimes]
  );

  return (
    <>
      <Widget>
        <Collapsible open={isExpanded} onOpenChange={onToggle}>
          <WidgetHeader
            title={mealTimeLabel}
            collapsible
            expanded={isExpanded}
            onToggle={onToggle}
            extras={
              hasFoodEntries ? (
                <NutrientReadings
                  energy={headerEnergy}
                  protein={summary?.protein}
                  carbs={summary?.carbs}
                  fat={summary?.fat}
                />
              ) : null
            }
          />

          <CollapsibleContent>
            <WidgetContent
              className={mealTime.entries.length === 0 ? "py-2" : undefined}
            >
              {mealTime.entries.length === 0 ? null : (
                <>
                  <Separator className="mb-4" />
                  <div className="space-y-2">
                    {mealTime.entries
                      .sort((a, b) => a.order - b.order)
                      .map((entry) => (
                        <MealEntryRow
                          key={entry.id}
                          entry={entry}
                          appointmentId={appointmentId}
                          isTubeFeeding={isTubeFeeding}
                          isCompleted={isCompleted}
                          isMealPlan={isMealPlan}
                          mealTimes={mealTimes}
                          mealGroupLabel={mealTimeLabel}
                          externalMoveTargets={externalMoveTargets}
                        />
                      ))}
                  </div>
                </>
              )}

              {!isCompleted && (
                <div
                  className={cn(
                    "flex items-center gap-2",
                    mealTime.entries.length === 0
                      ? "justify-between"
                      : "justify-end pt-3"
                  )}
                >
                  {mealTime.entries.length === 0 && (
                    <span className="z-body text-muted-foreground">
                      No items added yet
                    </span>
                  )}
                  <div className="flex gap-2">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            onClick={() => setShowAddFood(true)}
                            aria-label="Add food"
                          >
                            <Plus />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Add food</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            onClick={() => setShowAddNote(true)}
                            aria-label="Add note"
                          >
                            <StickyNote />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Add note</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}
            </WidgetContent>
          </CollapsibleContent>
        </Collapsible>
      </Widget>

      {/* Dialogs */}
      <AddFoodDialog
        open={showAddFood}
        onClose={() => setShowAddFood(false)}
        mealGroupId={mealTime.id}
        mealTimeLabel={mealTimeLabel}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={isMealPlan}
        planIndex={planIndex}
        mealGroupOptions={mealGroupOptions}
      />
      <AddNoteDialog
        open={showAddNote}
        onClose={() => setShowAddNote(false)}
        mealGroupId={mealTime.id}
        mealTimeLabel={mealTimeLabel}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={isMealPlan}
      />
    </>
  );
};

// =============================================================================
// Main Component - DietRecallTab
// =============================================================================

export const DietRecallTab: React.FC = () => {
  const {
    appointmentId,
    isTubeFeeding,
    isCompleted,
    mealPlanData,
    selectedMealPlanIndex,
  } = useNutritionEvaluationContext();

  const [expandedMealTimes, setExpandedMealTimes] = useState<
    Record<number, boolean>
  >({});
  const [showAddFood, setShowAddFood] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [_selectedMealGroupId, _setSelectedMealGroupId] = useState<string>("");
  const [_selectedMealTimeLabel, _setSelectedMealTimeLabel] =
    useState<string>("");

  const { data: dietRecall, isLoading } = useDietRecall(
    appointmentId,
    isTubeFeeding
  );

  // Fetch health profile for TDEE, BMR, PA Calories (shown dimmed in Diet Recall)
  const { data: healthProfile } = useHealthProfile(appointmentId);

  // Handle meal time expansion
  const toggleMealTime = useCallback((index: number) => {
    setExpandedMealTimes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  // Get data based on feeding method
  const mealData = isTubeFeeding ? null : (dietRecall as MealData);
  const feedData = isTubeFeeding ? (dietRecall as FeedData) : null;
  const selectedMealPlanData = mealPlanData[
    isTubeFeeding ? 0 : selectedMealPlanIndex
  ] as MealData | FeedData | null;

  // Get meal times with proper sorting from API data
  // Note: Meal groups are created by the backend when feeding type is set
  const mealTimes = useMemo(() => {
    if (mealData?.meal_groups && mealData.meal_groups.length > 0) {
      return [...mealData.meal_groups].sort((a, b) => a.order - b.order);
    }
    return [];
  }, [mealData]);

  const mealPlanMoveTargets = useMemo<MoveTarget[]>(() => {
    if (isTubeFeeding) {
      const feedGroups = (selectedMealPlanData as FeedData | null)?.feed_groups;
      if (!feedGroups) return [];
      return feedGroups
        .filter(
          (group) =>
            !group.entries.some(
              (entry) =>
                !entry.is_comment &&
                (entry.food_item ||
                  entry.food_item_details ||
                  entry.feed_item_details)
            )
        )
        .map((group) => ({
          id: group.id,
          context: "meal_plan" as MoveTargetContext,
          label: group.label || group.time,
        }));
    }

    const groups = (selectedMealPlanData as MealData | null)?.meal_groups;
    if (!groups) return [];
    return [...groups]
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        id: group.id,
        context: "meal_plan" as MoveTargetContext,
        label: MEAL_TIME_DISPLAY[group.meal_time] || group.meal_time,
      }));
  }, [isTubeFeeding, selectedMealPlanData]);

  // Get total nutrients
  const totalNutrients =
    mealData?.total_nutrients || feedData?.total_nutrients || null;

  // Check if there are any food entries (not just comments) - to show/hide summary sections
  const hasFoodEntries = useMemo(() => {
    if (isTubeFeeding && feedData?.feed_groups) {
      return feedData.feed_groups.some((group) =>
        group.entries?.some((entry) => entry.food_item)
      );
    }
    if (mealTimes.length > 0) {
      return mealTimes.some((mealTime) =>
        mealTime.entries?.some((entry) => !entry.is_comment)
      );
    }
    return false;
  }, [isTubeFeeding, feedData, mealTimes]);

  // Handler for opening Add Food dialog from top button
  const _handleAddFoodClick = useCallback((mealTime: MealTime) => {
    _setSelectedMealGroupId(mealTime.id);
    _setSelectedMealTimeLabel(
      MEAL_TIME_DISPLAY[mealTime.meal_time] || mealTime.meal_time
    );
    setShowAddFood(true);
  }, []);

  // Handler for opening Add Note dialog from top button
  const _handleAddNoteClick = useCallback((mealTime: MealTime) => {
    _setSelectedMealGroupId(mealTime.id);
    _setSelectedMealTimeLabel(
      MEAL_TIME_DISPLAY[mealTime.meal_time] || mealTime.meal_time
    );
    setShowAddNote(true);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6 p-1">
        {/* Tube Feeding Action Buttons */}
        {!isCompleted && isTubeFeeding && (
          <div className="flex items-center justify-end">
            <Button onClick={() => setShowAddFeed(true)}>
              <Plus />
              Add Feed
            </Button>
          </div>
        )}

        {/* Meal Times / Feed Groups */}
        {isTubeFeeding ? (
          <TubeFeedingSection
            feedData={feedData}
            appointmentId={appointmentId}
            isCompleted={isCompleted}
            isMealPlan={false}
            allowDeleteEmptyFeed
            renderEntry={({ entry, feedGroups, mealGroupLabel }) => (
              <MealEntryRow
                key={entry.id}
                entry={entry}
                appointmentId={appointmentId}
                isTubeFeeding={true}
                isCompleted={isCompleted}
                feedGroups={feedGroups}
                mealGroupLabel={mealGroupLabel}
                externalMoveTargets={mealPlanMoveTargets}
              />
            )}
          />
        ) : (
          <div className="space-y-2">
            {mealTimes.length > 0 ? (
              mealTimes.map((mealTime, index) => (
                <MealTimeCard
                  key={mealTime.id}
                  mealTime={mealTime}
                  index={index}
                  appointmentId={appointmentId}
                  isTubeFeeding={isTubeFeeding}
                  isCompleted={isCompleted}
                  isExpanded={expandedMealTimes[index] || false}
                  onToggle={() => toggleMealTime(index)}
                  mealTimes={mealTimes}
                  externalMoveTargets={mealPlanMoveTargets}
                />
              ))
            ) : (
              <Widget>
                <WidgetContent className="py-8 text-center text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="z-body">No meal times configured.</p>
                  <p className="z-caption mt-1">
                    Please select a feeding type in Nutrition Profile first.
                  </p>
                </WidgetContent>
              </Widget>
            )}
          </div>
        )}

        {/* Energy Summary + Targets (combined in one card like Flutter) */}
        {/* In Diet Recall, Burns and Remaining charts are dimmed (isMealPlan=false) */}
        {/* Only show when at least one food item is added */}
        {totalNutrients && hasFoodEntries && (
          <EnergySummaryCard
            totalNutrients={totalNutrients}
            isMealPlan={false}
            tdee={healthProfile?.tdee || 0}
            bmr={healthProfile?.bmr || 0}
            paCalories={healthProfile?.pa_calories || 0}
          />
        )}

        {/* Featured Nutrients - Only show when at least one food item is added */}
        {totalNutrients && hasFoodEntries && (
          <FeaturedNutrientsCard totalNutrients={totalNutrients} />
        )}

        {/* Detailed Nutrients Tables (matching Flutter) */}
        {totalNutrients && (
          <DetailedNutrientsPanel totalNutrients={totalNutrients} />
        )}
      </div>

      {/* Top-level Dialogs with Meal Time Selector */}
      <AddFoodDialogWithSelector
        open={showAddFood}
        onClose={() => setShowAddFood(false)}
        mealTimes={mealTimes}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={false}
      />
      <AddNoteDialogWithSelector
        open={showAddNote}
        onClose={() => setShowAddNote(false)}
        mealTimes={mealTimes}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={false}
      />
      <AddFeedDialog
        open={showAddFeed}
        onClose={() => setShowAddFeed(false)}
        appointmentId={appointmentId}
        isMealPlan={false}
      />
    </ScrollArea>
  );
};

export default DietRecallTab;
